import type { components } from '@scalty/contracts';
import { formatBytes, deriveStatus } from '~/utils/dataObjects';
import { formatRelativeTime } from '~/utils/relativeTime';
import { formatShortDate } from '~/utils/shortDate';
import { sortRows } from '~/utils/sortRows';
import { formatMoney } from '~/utils/money';
import { formatBilledMinutes, formatRunDuration } from '~/utils/trainingRun';
import { formatLoss, trainingStageLine, trainingStatusWord } from '~/utils/trainingProgress';
import { DATA_WORDS, trainingExecutionWord } from '~/utils/state';
import { kindLabel } from '~/utils/datasetKinds';
import { plural } from '~/utils/words';
import { acceleratorLabel } from '~/utils/accelerators';
import { hyperparamRows } from '~/utils/hyperparams';
import { loadOrganizationView } from './studioOrganization';
import { loadEntityView, deploymentRow, memoryRow, meteredFromGroup } from './studioEntities';

type S = components['schemas'];
export interface StudioContext {
  projectId: string | null;
  get<T>(path: string): Promise<T>;
  query: Record<string, string | number>;
}
const word = (value: string) => value.replaceAll('_', ' ').replace(/^./, (letter) => letter.toUpperCase());
const cellDate = (input?: string | null) => input ? formatShortDate(input) : '—';
const time = (value?: string | null) => value ? formatRelativeTime(value) : '—';
const size = (value?: number | null) => value == null ? '—' : formatBytes(value);
const status = (model: S['Model']) => model.archivedAt ? 'Archived' : trainingStatusWord(model);

export async function collect<T>(ctx: StudioContext, path: string, key: string): Promise<T[]> {
  const rows: T[] = [];
  let cursor: string | null = null;
  const seen = new Set<string>();
  do {
    const result: Record<string, unknown> & { nextCursor: string | null } = await ctx.get<Record<string, unknown> & { nextCursor: string | null }>(`${path}${path.includes('?') ? '&' : '?'}limit=200${cursor ? `&cursor=${encodeURIComponent(cursor)}` : ''}`);
    const page = result[key];
    if (!Array.isArray(page)) throw new Error(`The ${key} list returned an invalid response.`);
    rows.push(...page as T[]);
    cursor = result.nextCursor;
    if (cursor && seen.has(cursor)) throw new Error('The API repeated a pagination cursor.');
    if (cursor) seen.add(cursor);
  } while (cursor);
  return rows;
}
/**
 * Which words one chip stands for. A chip usually names a single word, and the entries here are
 * the ones that name a stage rather than a word: `Building` is every step of making something out
 * of what was put in, `Training` is a run whether it is waiting for a machine or already running.
 */
const FILTER_WORDS: Record<string, string[]> = { working: ['validating','running','training'], preparing: ['uploading','creating','preparing'], writing: ['running','writing','in progress'], /* Preparing the data and waiting for a machine are both stages of training, so the Training
       chip finds a run that is in either of them. */
  training: ['training','in progress','preparing','waiting'], queued: ['queued'], waiting_for_documents: ['waiting for documents'], failed: ['failed','upload_failed','did not finish'], building: ['creating','reindexing','building','preparing','uploading','syncing','writing'], invited: ['pending'] };

/**
 * Whether one row's word belongs to one chip. The count on a chip and the rows behind it ask this
 * same question, because they once asked different ones: the dataset list counted the rows wearing
 * the word `Building` and then filtered by a chip that also accepted Preparing, Uploading, Syncing
 * and Writing, so the chip promised one row and produced four (W8-UI-09). A number a customer
 * decides by must be the number they get.
 */
export function statusMatches(status: string | undefined, filter: string): boolean {
  if (!filter) return true;
  const accepted = FILTER_WORDS[filter.toLowerCase()] ?? [filter.toLowerCase()];
  return accepted.includes((status ?? '').toLowerCase());
}

/** How many rows one chip stands for, asked exactly as the filter asks it. */
export function facetCount<T>(rows: T[], word: (row: T) => string, filter: string): number {
  return rows.filter((row) => statusMatches(word(row), filter)).length;
}

export function pageRows<T extends { name?: string; status?: string }>(ctx: StudioContext, rows: T[]) {
  const search = String(ctx.query.q ?? '').toLowerCase();
  const filter = String(ctx.query.state ?? '');
  const visible = rows.filter((row) => (!search || row.name?.toLowerCase().includes(search)) && statusMatches(row.status, filter));
  /* The Sort menu names a field of these rows (Column.sort); one rule orders every list. */
  const ordered = sortRows(visible, typeof ctx.query.sort === 'string' ? ctx.query.sort : undefined);
  const perPage = Number(ctx.query.perPage ?? 10);
  const page = Math.max(1, Number(ctx.query.page ?? 1));
  return { total: ordered.length, rows: ordered.slice((page - 1) * perPage, page * perPage) };
}
const projectPath = (ctx: StudioContext, path: string) => `${path}?project=${encodeURIComponent(ctx.projectId ?? '')}`;

/* Memory collections of the project, read once per page so each row can say where it is used. */
export type DocumentUsage = { projections: { collection: S['KnowledgeBase']; documentIds: string[] }[]; /* files per Add data session, keyed by upload batch */ batches?: Map<string, number> };
export async function documentUsage(ctx: StudioContext): Promise<DocumentUsage> {
  if (!ctx.projectId) return { projections: [] };
  const collections = await collect<S['KnowledgeBase']>(ctx, projectPath(ctx, '/knowledge-bases'), 'knowledgeBases');
  const projections = await Promise.all(collections.map(async (collection) => ({ collection, documentIds: (await collect<S['Document']>(ctx, `/knowledge-bases/${encodeURIComponent(collection.id)}/documents`, 'documents')).flatMap((document) => document.sourceDataObjectId ? [document.sourceDataObjectId] : []) })));
  return { projections };
}
export async function documentRow(ctx: StudioContext, object: S['DataObject'], usage: DocumentUsage = { projections: [] }) {
  const linked = usage.projections.filter((entry) => entry.documentIds.includes(object.id)).map((entry) => entry.collection);
  const base = `/data-objects/${encodeURIComponent(object.id)}`;
  const revisionList = await ctx.get<S['DataObjectRevisionList']>(`${base}/revisions?limit=20`);
  /* The newest revision decides whether an upload is still arriving; the current one is the file the object holds. */
  const newest = revisionList.dataObjectRevisions[0] ?? null;
  const revision = revisionList.dataObjectRevisions.find((entry) => entry.id === object.currentRevisionId) ?? newest;
  const derived = deriveStatus({ revision: newest, currentRevisionId: object.currentRevisionId });
  return {
    id: object.id, name: object.displayName, revision: revision?.revisionNumber ?? '—', collection: object.uploadBatchId && usage.batches?.get(object.uploadBatchId) ? plural(usage.batches.get(object.uploadBatchId)!, 'file') : 'One file', contentType: revision?.contentType ?? '—',
    status: derived?.label ?? 'Not started', size: size(revision?.sizeBytes ?? revision?.expectedSizeBytes), updated: cellDate(object.createdAt),
    memories: linked.length, revisions: revisionList.dataObjectRevisions.map((rev) => ({ n: rev.revisionNumber, current: rev.id === object.currentRevisionId, size: size(rev.sizeBytes ?? rev.expectedSizeBytes), at: time(rev.createdAt) })),
    usedIn: linked.map((collection) => ({ name: collection.name, status: memoryWord(collection), fact: 'memory collection' })),
    raw: { object, revision },
  };
}
/* The list's evaluation column is the held-out headline: the share of answerable questions the version got right. */
const headline = (model: S['Model']) => model.heldOutEvaluation?.summary?.overall.tuned.accuracy ?? null;
/** The base model in the words the train dialog offered it in; a tabular run started from none. */
const baseModelLabel = (model: S['Model']) => model.baseModel ?? (model.modelType === 'text_generation' ? '—' : 'None · trained from the data alone');
/** The machine the run used, or the honest absence: a tabular run has no accelerator at all.
 *  The class is read through the one label map, so no screen shows a stored identifier. */
const machineLabel = (model: S['Model']) => model.acceleratorClass ? acceleratorLabel(model.acceleratorClass) : model.modelType === 'text_generation' ? '—' : 'None · runs on a processor';
/**
 * The loss curve of a run that has ended, read from the measurements the trainer wrote.
 *
 * `trainingProgress` is the live projection and ends with the run; `metrics` is what the
 * trainer reports when it finishes — `train_loss` and `eval_loss` as `[step, value]` pairs
 * plus the first and last evaluation. Reading only the live one made the page tell every
 * finished run that its curve had not been kept, while the curve was in the same response
 * it had already fetched (W8-DS-24 #4).
 */
function finishedLoss(model: S['Model']) {
  const metrics = model.metrics as Record<string, unknown> | null | undefined;
  if (!metrics) return null;
  const series = (key: string): [number, number][] => {
    const raw = metrics[key];
    if (!Array.isArray(raw)) return [];
    return raw.flatMap((point) => Array.isArray(point) && point.length === 2
      && typeof point[0] === 'number' && Number.isFinite(point[0])
      && typeof point[1] === 'number' && Number.isFinite(point[1])
      ? [[point[0], point[1]] as [number, number]] : []);
  };
  const train = series('train_loss');
  const evaluation = series('eval_loss');
  if (train.length === 0 && evaluation.length === 0) return null;
  const number = (key: string) => typeof metrics[key] === 'number' && Number.isFinite(metrics[key] as number) ? metrics[key] as number : undefined;
  const lastStep = Math.max(train.at(-1)?.[0] ?? 0, evaluation.at(-1)?.[0] ?? 0);
  return {
    trainNow: formatLoss(train.at(-1)?.[1]),
    evalNow: formatLoss(number('final_eval_loss') ?? evaluation.at(-1)?.[1]),
    totalSteps: lastStep,
    currentStep: lastStep,
    train,
    eval: evaluation,
  };
}

/**
 * The measured share of a training run, from the only pair the contract carries for one:
 * the optimizer step the trainer last completed out of the total it resolved. A run that
 * has measured nothing, or one whose trainer never resolved a total, has no share — the
 * screen then draws the indeterminate bar and says "—", never a number nobody measured.
 * `trainingProgress` reaches the portal from `GET /models/{id}` alone, so every caller of
 * this helper has to hold that read; a list row does not carry one.
 */
export function trainingPercent(progress: S['TrainingProgress'] | null | undefined): number | null {
  return progress && progress.maxSteps > 0 ? Math.round(progress.step / progress.maxSteps * 100) : null;
}

/**
 * The run's own journal: every entry the training chain recorded, in the order it recorded
 * them, each with the moment it happened. This is the only ordered, time-stamped series the
 * contract already carries for a piece of work, and it is read exactly as it comes — one
 * entry for a run that settled first time, one more for every relaunch after a lost machine.
 * The words are the product's; `settled`, `relaunched` and `charge_waived` never reach a
 * screen.
 */
export function trainingExecutionRows(run: S['TrainingRun'] | null | undefined) {
  return (run?.executions ?? []).map((entry, index) => ({
    /* The chain has no key of its own; its order is its identity. */
    id: String(index),
    at: cellDate(entry.at),
    event: trainingExecutionWord(entry.event, entry.state),
    /* Why the entry exists, where the run said so: the settle message, or the fact that the
       relaunch carried the finished work over instead of starting again from nothing. */
    note: entry.error ?? (entry.event === 'relaunched' ? (entry.resume ? 'Continued from the last saved point' : 'Started again from the beginning') : failureNote(entry.failureAttribution)),
    runtime: formatRunDuration(entry.wallSeconds),
    billed: formatBilledMinutes(entry.minutes),
  }));
}
/* Who the run's end was attributed to, said once, in the customer's terms. */
const failureNote = (attribution: S['TrainingExecution']['failureAttribution']) =>
  attribution === 'platform' ? 'Attributed to the platform' : attribution === 'customer' ? 'Attributed to the run’s own data or settings' : '';

export function modelRow(model: S['Model'], models: S['Model'][], datasets: S['Dataset'][] = []) {
  const progress = model.trainingProgress;
  const accuracy = headline(model);
  const best = Math.max(...models.filter((other) => other.name === model.name).map((other) => headline(other) ?? -1));
  /* While the run is going the live projection is the curve; once it has ended, the trainer's
     own measurements are, and they outlive the run. */
  const loss = progress ? { trainNow: formatLoss(progress.trainLoss.latest), evalNow: formatLoss(progress.evalLoss.at(-1)?.value), totalSteps: progress.maxSteps, currentStep: progress.step,
    train: progress.trainLoss.series.map((point) => [point.step, point.value]), eval: progress.evalLoss.map((point) => [point.step, point.value]) } : finishedLoss(model);
  /* What the version was made from, so a customer can repeat or compare it (founder, 2026-09-21,
     note 32): the model it started from, the data it read and the settings it read them with.
     A dataset the caller passed is named; without that list the row still says which one, by the
     only handle it holds. */
  const trainedOn = datasets.find((set) => set.id === model.datasetId) ?? null;
  const config = [
    ['Base model', baseModelLabel(model)],
    ['Training set', trainedOn?.name ?? model.datasetId],
    ['Accelerator', machineLabel(model)],
    ['Duration', formatRunDuration(model.training?.durationSeconds)],
    /* The settings the run used, through the one label map the train dialog shows them with —
       never the stored identifier ("LearningRate", "MaxSeqLen"). A tabular run resolves none, and
       then the block simply has no such rows. */
    ...(model.hyperparams ? hyperparamRows(model.hyperparams).map((row) => [row.label, row.value]) : []),
  ];
  return { id: model.id, name: model.name, version: `v${model.version}`, status: status(model), percent: trainingPercent(progress),
    base: model.baseModel ?? '—', eval: accuracy == null ? '—' : `${(accuracy * 100).toFixed(1)}%`, evalBest: accuracy != null && accuracy >= best && models.filter((other) => other.name === model.name && headline(other) != null).length > 1, time: formatRunDuration(model.training?.durationSeconds), made: cellDate(model.createdAt),
    /* The stage line carries the run's measured step when the read that holds it is the one
       the row was built from; the field the contract names it under is `trainingProgress`. */
    stage: trainingStageLine({ ...model, progress: model.trainingProgress }), problem: model.error, elapsed: formatRunDuration(model.training?.durationSeconds), pace: progress?.tokensPerSecond ? String(Math.round(progress.tokensPerSecond)) : '—',
    /* How long the customer waited before the accelerator began, from the moment they pressed
       Train to the moment the run started. It is not part of the training time and was not shown
       anywhere, so a half-hour wait read as a five-minute run. What the wait was spent on is a
       separate question (W8-UI-05); this is only the wait itself, and it is never guessed. */
    ...waitedBeforeStart(model),
    /* The moment the running stage began, so the page can count from it while the run is in
       flight. Preparation and the wait for a machine each start their own clock; a run that has
       started counts from its container. */
    since: model.training?.startedAt ?? model.trainingWait?.waitingSince ?? model.createdAt,
    /* And the moment the run itself began. The page used to count only from the current stage, so
       the figure fell back to zero at every transition and a reader could not tell a ten-minute
       run from an hour-long one (W8-UI-10, from the owner's rehearsal). */
    runSince: model.createdAt,
    cost: model.training?.cost ? formatMoney(model.training.cost) : '—', machine: [machineLabel(model), model.acceleratorCount ? plural(model.acceleratorCount, 'accelerator') : ''], loss, config,
    versions: models.filter((other) => other.name === model.name).map((other) => ({ label: `v${other.version}`, status: status(other), fact: time(other.createdAt) })),
    versionsFoot: plural(models.filter((other) => other.name === model.name).length, 'version'), after: [['Evaluation', model.heldOutEvaluation ? word(model.heldOutEvaluation.status) : 'Not measured']], raw: model };
}
/**
 * The wait before the accelerator started, and the words under it.
 *
 * A run that started once is the difference between pressing Train and the container
 * starting. A relaunched run is not: the new container re-stamps `startedAt`, so that
 * difference would silently swallow the earlier attempts. The contract records how many
 * attempts there were but not when each began, so a multi-attempt run reports no wait
 * rather than a number computed here and shown as measured.
 */
function waitedBeforeStart(model: S['Model']): { waited: string; waitedUnit: string } {
  const run = model.training;
  if (run?.startedAt) {
    if (run.attempt > 1 || run.chainCount > 1) return { waited: '—', waitedUnit: 'the run started more than once' };
    return { waited: formatRunDuration((Date.parse(run.startedAt) - Date.parse(model.createdAt)) / 1000), waitedUnit: 'before it started' };
  }
  if (model.trainingWait?.waitedSeconds != null) return { waited: formatRunDuration(model.trainingWait.waitedSeconds), waitedUnit: 'waiting so far' };
  return { waited: '—', waitedUnit: 'not started yet' };
}
/**
 * The dataset's word, as the server decided it.
 *
 * Where a dataset stands used to be worked out here, by combining the container's stored status
 * with the number of files in it — and the dataset's own page worked out a second answer from the
 * files themselves. One dataset then read `Ready` in the list and `Checking` on its card at the
 * same moment (W8-UI-07). The verdict now arrives on the dataset as `readiness`, decided once in
 * the datasets domain, and this is the one place that turns it into the word on screen.
 */
export function datasetWord(set: S['Dataset']) {
  return DATA_WORDS.datasetReadiness[set.readiness.state] ?? word(set.readiness.state);
}
/**
 * One memory collection, in one word. The same story as the dataset's: the stored status says the
 * collection is ready to accept documents, and a reader that showed it told a customer their
 * memory was ready while the deployment bound to it refused to quote from it (W8-UI-09). The
 * verdict arrives on the collection as `readiness`, decided once in the knowledge-bases domain,
 * and this is the one place that turns it into the word on screen.
 */
export function memoryWord(collection: S['KnowledgeBase']) {
  return DATA_WORDS.memoryReadiness[collection.readiness.state] ?? word(collection.readiness.state);
}
/**
 * Whether anything in the collection can be quoted, asked of the one verdict the server
 * decided. Surfaces that describe grounding ask this instead of assuming a bound collection
 * answers: the deployment page said "1 collection consulted" and the playground promised
 * answers "from" a collection whose own word beside it was Empty (W8-UI-10).
 */
export function memoryQuotable(collection: S['KnowledgeBase'] | null | undefined) {
  return collection?.readiness.state === 'ready';
}
/**
 * One row of the dataset list. Every figure comes from the container itself — W8-DS-08 made
 * `memberCount`, `sourceCount` and `currentVersion` real — so a list of any length costs one
 * request. Nothing here is counted by reading a second list.
 */
export function datasetRow(set: S['Dataset'], models: S['Model'][]) {
  const current = set.currentVersion;
  const label = datasetWord(set);
  return {
    id: set.id, name: set.name, kind: set.kind, type: kindLabel(set.kind), status: label,
    files: set.memberCount, sources: set.sourceCount, size: size(set.sizeBytes), updated: cellDate(set.createdAt),
    /* A version number is a fact; the absence of one is said in words, never as v0 or a zero. */
    versionLabel: current ? `v${current.version} · ${current.rowCount == null ? 'rows not measured' : plural(current.rowCount, 'row')}` : 'No version yet',
    /* A build states what it has measured on the version's own read, and the list does not make
       that read: finding the building version and then reading it would be two requests per row.
       The dataset's own page shows the measurement; a row here draws no percentage for one. */
    percent: null as number | null,
    rowCount: set.rowCount, memberCount: set.memberCount, sourceCount: set.sourceCount, sizeBytes: set.sizeBytes, createdAt: set.createdAt,
    stats: {
      files: [String(set.memberCount), set.memberCount === 1 ? 'file in the dataset' : 'files in the dataset'] as [string, string],
      rows: [current?.rowCount == null ? 'Not measured' : String(current.rowCount), 'in the current version'] as [string, string],
      sources: [String(set.sourceCount), set.sourceCount === 1 ? 'connector attached' : 'connectors attached'] as [string, string],
      size: [size(set.sizeBytes), 'stored'] as [string, string],
    },
    current: current
      ? [
          { label: 'Version', value: `v${current.version}` },
          { label: 'Built', value: time(current.completedAt ?? current.createdAt) },
          { label: 'Training rows', value: current.rowCount == null ? 'Not measured' : String(current.rowCount) },
          { label: 'Files in', value: String(current.inputCount) },
        ]
      : [{ label: 'Version', value: 'No version yet' }],
    usedIn: models.filter((model) => model.datasetId === set.id).map((model) => ({ label: `${model.name} v${model.version}`, status: status(model), fact: time(model.createdAt) })),
    raw: set,
  };
}

/** Where one of the overview's controls leads: a dialog the studio opens, or a page. */
export type OverviewGo =
  | { dialog: 'dataset' | 'dataset-files' | 'train' | 'deploy' | 'key'; target?: { id: string; name?: string; slug?: string; kind: string } }
  | { to: string };
type OverviewStepState = 'done' | 'current' | 'todo';
/**
 * The project's path in the four steps of the product model's zero version: files in a dataset,
 * a trained model, a deployment, and a key to call it with (PRODUCT_MODEL, "The zero version").
 *
 * Preparing the training data is the run's own first stage (founder, 2026-09-21), so it is not a
 * step here, and the data step is done once the server says a dataset can be trained on — not
 * once a version exists, which only a run builds. Until W9-PORTAL-UX the strip waited for a
 * version and told a customer who had just made a dataset to make one.
 *
 * The step the customer stands on also decides the page's one gradient action and the sentence
 * under "Next", so the three always point the same way.
 */
export function overviewPath(input: {
  datasets: S['Dataset'][];
  models: S['Model'][];
  deployments: S['Deployment'][];
  keys: S['ApiKeySummary'][];
  progress: Map<string, S['TrainingProgress'] | null | undefined>;
  projectId: string | null;
  now: number;
}) {
  const { datasets, deployments } = input;
  const models = input.models.filter((model) => !model.archivedAt);
  const trainable = datasets.find((set) => set.readiness.state === 'ready') ?? null;
  const dataset = trainable ?? datasets.find((set) => set.memberCount > 0) ?? datasets[0] ?? null;
  const running = models.find((model) => model.status === 'queued' || model.status === 'training') ?? null;
  const trained = models.find((model) => model.status === 'succeeded') ?? null;
  const serving = deployments.find((entry) => entry.state === 'active') ?? null;
  const deployment = serving ?? deployments[0] ?? null;
  /* A key that can call this project's deployment: one bound to it, or an organisation key that no
     project restriction keeps out. Revoked and expired keys cannot. */
  const ids = new Set(deployments.map((entry) => entry.id));
  const key = input.keys.find((entry) => entry.isActive
    && (entry.expiresAt === null || Date.parse(entry.expiresAt) > input.now)
    && (entry.scope.kind === 'deployment' ? ids.has(entry.scope.deploymentId ?? '') : entry.projectId === null || entry.projectId === input.projectId)) ?? null;

  const done = [Boolean(trainable || running || trained), Boolean(trained), Boolean(serving), Boolean(serving && key)];
  const current = done.findIndex((step) => !step);
  const state = (index: number): OverviewStepState => done[index] ? 'done' : index === current ? 'current' : 'todo';
  const version = (model: S['Model']) => `${model.name} v${model.version}`;
  const toDataset = (set: S['Dataset']) => ({ id: set.id, name: set.name, kind: 'dataset' });
  const toDeployment = (entry: S['Deployment']) => ({ id: entry.slug, slug: entry.slug, name: entry.name, kind: 'deployment' });

  const data = dataset
    ? { entity: dataset.name, facts: `${plural(dataset.memberCount, 'file')} · ${datasetWord(dataset)}`,
        action: dataset.memberCount > 0 ? 'Open dataset' : 'Add files', actionIcon: dataset.memberCount > 0 ? 'arrow-right' : 'plus',
        go: dataset.memberCount > 0 ? { to: `/datasets/${dataset.id}` } : { dialog: 'dataset-files', target: toDataset(dataset) } }
    : { entity: 'Add documents', facts: 'No dataset yet', action: 'New dataset', actionIcon: 'plus', go: { dialog: 'dataset' } };
  const train = running
    ? { entity: version(running), facts: trainingStageLine({ ...running, progress: input.progress.get(running.id) }), status: status(running),
        percent: trainingPercent(input.progress.get(running.id)), action: 'Open run', actionIcon: 'arrow-right', go: { to: `/models/${running.id}` } }
    : trained
      ? { entity: version(trained), facts: `Ready · ${plural(models.length, 'version')}`, action: 'Try in Playground', actionIcon: 'playground', go: { to: `/playground?model=${encodeURIComponent(trained.id)}` } }
      : { entity: 'Train a model', facts: models[0]?.status === 'failed' ? `${version(models[0])} did not finish` : 'No model yet',
          action: 'Train model', actionIcon: 'play', go: { dialog: 'train', ...(trainable ? { target: toDataset(trainable) } : {}) } };
  const deploy = deployment
    ? { entity: deployment.name, facts: `${DATA_WORDS.deployment[deployment.state]} · ${plural(deployments.length, 'deployment')}`, action: 'Open deployment', actionIcon: 'arrow-right', go: { to: `/deploy/${encodeURIComponent(deployment.slug)}` } }
    : { entity: trained ? `Deploy ${trained.name}` : 'Deploy a model', facts: 'No deployment yet', action: 'Deploy', actionIcon: 'deploy',
        go: { dialog: 'deploy', ...(trained ? { target: { id: trained.id, name: trained.name, kind: 'model' } } : {}) } };
  const connect = key && deployment
    ? { entity: key.name, facts: `API key ${key.keyPrefix}…`, action: 'Open deployment', actionIcon: 'arrow-right', go: { to: `/deploy/${encodeURIComponent(deployment.slug)}` } }
    : { entity: 'Create a key', facts: 'Address and key for your site or app', action: 'Create key', actionIcon: 'key',
        go: deployment ? { dialog: 'key', target: toDeployment(deployment) } : { to: '/deploy' } };

  /* A step not reached yet leads to its section rather than to a dialog that has nothing to act on. */
  const sections = ['/datasets', '/models', '/deploy', '/deploy'];
  const stages = [
    { n: '01', icon: 'datasets', label: 'Data', ...data },
    { n: '02', icon: 'models', label: 'Train', ...train },
    { n: '03', icon: 'deploy', label: 'Deploy', ...deploy },
    { n: '04', icon: 'globe', label: 'Connect', ...connect },
  ].map((stage, index) => ({ ...stage, state: state(index), go: (state(index) === 'todo' ? { to: sections[index] } : stage.go) as OverviewGo }));

  /* Why the dataset cannot be trained on yet is the server's sentence when it has one. */
  const next = [
    !dataset ? 'Create a dataset and put your documents in it.' : dataset.memberCount === 0 ? `Put your documents in ${dataset.name}.` : dataset.readiness.reason ?? `Open ${dataset.name} to see where its files stand.`,
    running ? `${version(running)} is running; its page shows each stage.` : `Train a model on ${trainable?.name ?? 'your dataset'}.`,
    deployment ? `${deployment.name} is ${DATA_WORDS.deployment[deployment.state].toLowerCase()}; its page says what it needs.` : `Deploy ${trained?.name ?? 'the model'} to get an address to call.`,
    `Create a key for ${deployment?.name ?? 'the deployment'} and call its address from your server.`,
  ][current] ?? 'Add new data and train again; a deployment switches to the new version in one step.';
  /* The one gradient action is the current step's own. With every step done the loop starts over:
     new data, a new version. */
  const primary = current === -1
    ? { label: 'Train model', icon: 'play', go: { dialog: 'train' } as OverviewGo }
    : { label: stages[current]!.action, icon: stages[current]!.actionIcon, go: stages[current]!.go };
  return { stages, primary, next, meta: current === -1 ? 'all four steps done' : `step ${current + 1} of 4` };
}

/**
 * The line under a project page's title: what the customer wrote about the project, and that it
 * is archived when it is. The project's identifier stood here and told nobody anything (W9-UI-01
 * dev walk); a project without a description has no line rather than a placeholder.
 */
export function projectLine(project: Pick<S['Project'], 'description' | 'archivedAt'> | undefined): string {
  if (!project) return 'No project';
  return [project.description?.trim() ?? '', project.archivedAt ? 'archived' : ''].filter(Boolean).join(' · ');
}
/** The current project's line, read from the same project list the overview reads. */
export async function projectMeta(ctx: StudioContext): Promise<string> {
  if (!ctx.projectId) return 'No project';
  const projects = (await ctx.get<S['ProjectList']>('/projects?includeArchived=true')).projects;
  return projectLine(projects.find((item) => item.id === ctx.projectId));
}

export async function loadStudioView(ctx: StudioContext, path: string): Promise<unknown> {
  if (path.startsWith('/api/org-') || path === '/api/profile' || path === '/api/settings' || path === '/api/nav' || path.startsWith('/api/invitations/')) return loadOrganizationView(ctx, path);
  const models = ctx.projectId ? await collect<S['Model']>(ctx, projectPath(ctx, '/models'), 'models') : [];
  if (path === '/api/models') {
    /* Archived versions stay out of every other view; the list shows them only on request. */
    const versions = ctx.query.archived === 'shown' && ctx.projectId ? await collect<S['Model']>(ctx, `${projectPath(ctx, '/models')}&includeArchived=true`, 'models') : models;
    /* A version says what it was trained on by name, not by the id it stores it under. */
    const datasets = ctx.projectId ? await collect<S['Dataset']>(ctx, projectPath(ctx, '/datasets'), 'datasets') : [];
    const listed = pageRows(ctx, versions.map((model) => modelRow(model, versions, datasets)));
    const rows = await Promise.all(listed.rows.map(async (row) => modelRow((await ctx.get<S['ModelEnvelope']>(`/models/${encodeURIComponent(row.id)}`)).model, models, datasets)));
    /* The chips count the word the rows wear, so a run in preparation is findable under the
       stage it is in rather than falling out of every filter. */
    const wordCount = (filter: string) => facetCount(models, status, filter);
    return { ...listed, rows, meta: await projectMeta(ctx), counts: plural(listed.total, 'version'), archived: versions.filter((model) => model.archivedAt).length,
      facets: { ready: wordCount('Ready'), training: wordCount('Training'), queued: wordCount('Queued'), failed: wordCount('Failed') } };
  }
  if (path === '/api/data') {
    if (!ctx.projectId) return { meta: 'No project', counts: '0 documents', facets: { ready: 0, reading: 0, review: 0, refused: 0 }, total: 0, rows: [] };
    const state = ({ Ready: 'ready', Uploading: 'checking', Finalizing: 'checking', Expired: 'refused' } as Record<string,string>)[String(ctx.query.state ?? '')];
    const objects = await collect<S['DataObject']>(ctx, `${projectPath(ctx, '/data-objects')}${state ? `&state=${state}` : ''}${ctx.query.q ? `&q=${encodeURIComponent(String(ctx.query.q))}` : ''}`, 'dataObjects');
    const listed = pageRows({ ...ctx, query: { ...ctx.query, state: '' } }, objects.map((object) => ({ ...object, name: object.displayName })));
    const [usage, first] = await Promise.all([documentUsage(ctx), ctx.get<S['DataObjectList']>(`${projectPath(ctx, '/data-objects')}&limit=1`)]);
    /* The upload column says how many files arrived in the same Add data session; the batch id itself is not a name. */
    usage.batches = objects.reduce((map, object) => object.uploadBatchId ? map.set(object.uploadBatchId, (map.get(object.uploadBatchId) ?? 0) + 1) : map, new Map<string, number>());
    const rows = await Promise.all(listed.rows.map((object) => documentRow(ctx, object, usage)));
    if (!first.counts) throw new Error('Document totals were not returned by the API.');
    return { ...listed, rows, meta: await projectMeta(ctx), counts: plural(objects.length, 'document'), facets: { ready: first.counts.ready, reading: first.counts.checking, review: 0, refused: first.counts.refused } };
  }
  if (path === '/api/datasets') {
    const sets = ctx.projectId ? await collect<S['Dataset']>(ctx, projectPath(ctx, '/datasets'), 'datasets') : [];
    const listed = pageRows(ctx, sets.map((set) => datasetRow(set, models)));
    /* The chips count the word the rows actually wear, so a filter can never offer a count for
       a state the list does not show under that name. */
    const chip = (filter: string) => facetCount(sets, datasetWord, filter);
    return { ...listed, meta: await projectMeta(ctx), counts: plural(sets.length, 'dataset'),
      /* One chip per word the verdict can give. Checking and Not readable had no chip at all, so a
         dataset being read, or one whose files could not be read, was in the list and selectable
         from nothing (W8-UI-09). */
      facets: { ready: chip('Ready'), building: chip('Building'), checking: chip('Checking'), unreadable: chip('Not readable'), empty: chip('Empty'), failed: chip('Failed') } };
  }
  if (path === '/api/deployments') {
    const deployments = ctx.projectId ? await collect<S['Deployment']>(ctx, projectPath(ctx, '/deployments'), 'deployments') : [];
    /* The state chips filter on the row's status word, so the raw state is named before paging. */
    const listed = pageRows(ctx, deployments.map((deployment) => ({ ...deployment, status: DATA_WORDS.deployment[deployment.state] })));
    /* Metered volume and spend per endpoint come from the usage ledger for the chosen month. */
    const period = typeof ctx.query.period === 'string' && /^\d{4}-\d{2}$/.test(ctx.query.period) ? ctx.query.period : new Date().toISOString().slice(0, 7);
    const usage = deployments.length ? await collect<S['UsageGroup']>(ctx, `/usage/breakdown?groupBy=deployment&period=${period}`, 'groups') : [];
    /* A bound collection is named in the pane, so the project's collections are read once. */
    const collections = deployments.length ? await collect<S['KnowledgeBase']>(ctx, projectPath(ctx, '/knowledge-bases'), 'knowledgeBases') : [];
    return { ...listed, rows: await Promise.all(listed.rows.map((deployment) => deploymentRow(ctx, deployment, models, usage.find((group) => group.key === deployment.slug || group.key === deployment.id) ?? null, collections))), period, meta: await projectMeta(ctx), counts: plural(deployments.length, 'deployment'), /* One chip per state a deployment can be in, so an endpoint waiting for its documents is
       findable rather than counted as neither live nor paused (W8-UI-09). */
      facets: { live: deployments.filter((deployment) => deployment.state === 'active').length, paused: deployments.filter((deployment) => deployment.state === 'offline').length, waiting: deployments.filter((deployment) => deployment.state === 'waiting_for_documents').length } };
  }
  if (path === '/api/knowledge-bases') {
    const collections = ctx.projectId ? await collect<S['KnowledgeBase']>(ctx, projectPath(ctx, '/knowledge-bases'), 'knowledgeBases') : [];
    /* A chip stands for the readiness states the server decides, and the rows behind it are chosen
       by the same states, so a count and its rows cannot disagree. The shared filter words would
       match the status a collection is born with (`ready`) instead, and they do not know Indexing
       or Not quotable. */
    const chips: Record<string, S['KnowledgeBase']['readiness']['state'][]> = { Ready: ['ready'], Building: ['preparing', 'indexing', 'reindexing'], Empty: ['empty'], Failed: ['failed', 'unquotable'] };
    const chip = (name: string) => collections.filter((collection) => chips[name]!.includes(collection.readiness.state)).length;
    const state = String(ctx.query.state ?? '');
    const chosen = state && chips[state] ? collections.filter((collection) => chips[state]!.includes(collection.readiness.state)) : collections;
    /* Search and sort read the collection's own fields; each row on the page is then projected once. */
    const listed = pageRows({ ...ctx, query: { ...ctx.query, state: '' } }, chosen.map((collection) => ({ collection, name: collection.name, textSearchConfig: collection.textSearchConfig, createdAt: collection.createdAt })));
    return { total: listed.total, rows: await Promise.all(listed.rows.map((entry) => memoryRow(ctx, entry.collection))), meta: await projectMeta(ctx), counts: plural(collections.length, 'collection'),
      facets: { ready: chip('Ready'), building: chip('Building'), empty: chip('Empty'), failed: chip('Failed') } };
  }
  if (path === '/api/overview') {
    /* Archived projects are read here too: a customer who opened one has to find their own
       project named on the page they land on, not "No project" (W8-WALK). */
    const projects = (await ctx.get<S['ProjectList']>('/projects?includeArchived=true')).projects;
    const project = projects.find((item) => item.id === ctx.projectId);
    const documents = ctx.projectId ? await collect<S['DataObject']>(ctx, projectPath(ctx, '/data-objects'), 'dataObjects') : [];
    const deployments = ctx.projectId ? await collect<S['Deployment']>(ctx, projectPath(ctx, '/deployments'), 'deployments') : [];
    /* Step 02 is about the container the training data is prepared in, and that is a dataset. */
    const datasets = ctx.projectId ? await collect<S['Dataset']>(ctx, projectPath(ctx, '/datasets'), 'datasets') : [];
    /* The connect step asks whether a key can call the deployment. Keys belong to the organisation,
       so the list is read only once the project has a deployment for one to call. */
    const keys = deployments.length ? await collect<S['ApiKeySummary']>(ctx, '/api-keys', 'apiKeys') : [];
    /* The measured share of a run reaches the portal from GET /models/{id} alone — a list row
       always carries a null one. Only the versions that are actually training are re-read for
       it: a queued one has measured nothing yet, and a project holds a handful of running runs,
       never a page of them. The strip drew "—" for every run until this read (W8-UI-05 T1). */
    const running = models.filter((model) => ['training', 'queued'].includes(model.status));
    const measured = new Map(await Promise.all(running.filter((model) => model.status === 'training').map(async (model) =>
      [model.id, (await ctx.get<S['ModelEnvelope']>(`/models/${encodeURIComponent(model.id)}`)).model.trainingProgress] as const)));
    const jobs = running.map((model) => ({ id: model.id, entity: `Training · ${model.name} v${model.version}`, detail: model.baseModel ?? 'Tabular', status: status(model), stage: trainingStageLine({ ...model, progress: measured.get(model.id) }), percent: trainingPercent(measured.get(model.id)), elapsed: formatRunDuration(model.training?.durationSeconds), left: 'Not measured', cost: model.training?.cost ? formatMoney(model.training.cost) : '—', action: { label: 'View', icon: 'eye' }, to: `/models/${model.id}` }));
    /* The newest eight of everything the project has done. The total is the count of what
       they were drawn from, not the length of the slice: eight of eight would be a number the
       page invented. */
    const activity = [...documents.map((object) => ({ at: object.createdAt, tone: 'inactive', text: `Added ${object.displayName}`, ref: object.id, kind: 'data' })), ...models.map((model) => ({ at: model.createdAt, tone: 'inactive', text: `Created ${model.name} v${model.version}`, ref: model.id, kind: 'runs' }))].sort((a,b) => b.at.localeCompare(a.at)).slice(0,8).map((entry) => ({ ...entry, at: time(entry.at) }));
    /* The endpoints panel reads the same ledger the Deploy page reads, so one
       endpoint reports one figure at one moment wherever it is shown. */
    const deploymentUsage = deployments.length ? await collect<S['UsageGroup']>(ctx, `/usage/breakdown?groupBy=deployment&period=${new Date().toISOString().slice(0, 7)}`, 'groups') : [];
    const path = overviewPath({ datasets, models, deployments, keys, progress: measured, projectId: ctx.projectId, now: Date.now() });
    return { project: project?.name ?? 'Project', meta: projectLine(project), pipelineMeta: path.meta, stages: path.stages, primary: path.primary, jobsMeta: plural(jobs.length, 'job'), jobs, activity, activityTotal: documents.length + models.length, deployMeta: plural(deployments.length, 'deployment'), deployments: deployments.map((deployment) => {
      const group = deploymentUsage.find((entry) => entry.key === deployment.slug || entry.key === deployment.id) ?? null;
      return { id: deployment.id, slug: deployment.slug, status: DATA_WORDS.deployment[deployment.state], detail: deployment.name, metered: meteredFromGroup(group)[0], spend: group ? formatMoney(group.cost) : '—', warming: false };
    }), next: path.next };
  }
  return loadEntityView(ctx, path, models);
}
